import React, { useState, useEffect } from "react";
import "./App.css";
import RetroHeader from "./components/RetroHeader";
import RetroSidebar from "./components/RetroSidebar";
import RetroFooter from "./components/RetroFooter";
import AboutSection from "./components/sections/AboutSection";
import WorkSection from "./components/sections/WorkSection";
import ProjectsSection from "./components/sections/ProjectsSection";
import AchievementsSection from "./components/sections/AchievementsSection";
import SkillsSection from "./components/sections/SkillsSection";
import BlogSection from "./components/sections/BlogSection";
import ContactSection from "./components/sections/ContactSection";
import soundManager from "./utils/soundEffects";

function App() {
  const [activeSection, setActiveSection] = useState('home');
  const [darkMode, setDarkMode] = useState(false);
  const [backgroundLoaded, setBackgroundLoaded] = useState(false);

  useEffect(() => {
    // Load dark mode preference from localStorage
    const savedMode = localStorage.getItem('darkMode');
    if (savedMode === 'true') {
      setDarkMode(true);
    }
    
    // Preload background images
    const lightBg = new Image();
    const darkBg = new Image();
    lightBg.src = 'https://i.ibb.co/VYpr2Ws/ss134.gif';
    darkBg.src = 'https://i.ibb.co/LYhqxwq/ss175.gif';
    
    Promise.all([
      new Promise(resolve => {
        lightBg.onload = resolve;
        lightBg.onerror = resolve;
      }),
      new Promise(resolve => {
        darkBg.onload = resolve;
        darkBg.onerror = resolve;
      })
    ]).then(() => {
      setBackgroundLoaded(true);
    });
  }, []);

  const handleNavigate = (section) => {
    setActiveSection(section);
    soundManager.playWindowOpen();
  };

  const toggleDarkMode = () => {
    soundManager.playClick();
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem('darkMode', newMode.toString());
  };

  return (
    <div className={`App min-h-screen ${darkMode ? 'dark-mode' : ''}`}>
      {/* Dark Mode Toggle */}
      <button
        onClick={toggleDarkMode}
        className="dark-mode-toggle"
        aria-label="Toggle dark mode"
      >
        {darkMode ? '☀️' : '🌙'}
      </button>

      {/* Floating decorations */}
      <div className="fixed top-10 left-10 z-0">
        <span className="text-6xl animate-bounce">🧙‍♀️</span>
      </div>
      <div className="fixed top-20 right-20 z-0">
        <span className="text-5xl animate-pulse">✨</span>
      </div>
      <div className="fixed bottom-20 left-20 z-0" style={{ animationDelay: '1s' }}>
        <span className="text-5xl animate-bounce">🌙</span>
      </div>
      <div className="fixed bottom-10 right-10 z-0" style={{ animationDelay: '1s' }}>
        <span className="text-6xl animate-pulse">🔮</span>
      </div>
      <div className="fixed top-1/2 left-5 z-0">
        <span className="text-4xl animate-bounce" style={{ animationDelay: '0.5s' }}>⭐</span>
      </div>
      <div className="fixed top-1/3 right-10 z-0">
        <span className="text-4xl animate-pulse" style={{ animationDelay: '0.7s' }}>💫</span>
      </div>

      <div className="container mx-auto px-4 py-6 max-w-7xl">
        <div className="flex gap-6 relative z-10">
          {/* Sidebar */}
          <div className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-6">
              <RetroSidebar onNavigate={handleNavigate} />
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 main-content">
            <RetroHeader onNavigate={handleNavigate} />

            {/* Home/Welcome */}
            {activeSection === 'home' && (
              <div className="window mb-6">
                <div className="window-header">
                  <span>~ Welcome to my page! ~</span>
                </div>
                <div className="window-body text-center">
                  <div className="flex justify-center items-center gap-4 mb-4">
                    <span className="text-5xl animate-bounce">🌟</span>
                    <span className="text-6xl animate-pulse">👩‍💻</span>
                    <span className="text-5xl animate-bounce" style={{ animationDelay: '0.3s' }}>🌟</span>
                  </div>
                  <h2 className="text-3xl font-bold pixel-text mb-4" style={{ color: '#ff1493' }}>
                    Hey there, cyber friend! ✨
                  </h2>
                  <p className="text-sm mb-4 max-w-2xl mx-auto">
                    Welcome to my little corner of the internet! This is where I showcase my coding adventures, 
                    achievements, and all the cool stuff I've been working on. Feel free to explore and don't 
                    forget to sign my guestbook! 💖
                  </p>
                  <div className="flex justify-center gap-3 text-4xl mb-4">
                    <span className="animate-bounce">💻</span>
                    <span className="animate-pulse">🎵</span>
                    <span className="animate-bounce" style={{ animationDelay: '0.2s' }}>🌈</span>
                    <span className="animate-pulse" style={{ animationDelay: '0.4s' }}>✨</span>
                    <span className="animate-bounce" style={{ animationDelay: '0.6s' }}>🦄</span>
                  </div>
                </div>
              </div>
            )}

            {/* Content Sections */}
            {activeSection === 'about' && <AboutSection />}
            {activeSection === 'work' && <WorkSection />}
            {activeSection === 'projects' && <ProjectsSection />}
            {activeSection === 'achievements' && <AchievementsSection />}
            {activeSection === 'skills' && <SkillsSection />}
            {activeSection === 'writings' && <BlogSection />}
            {activeSection === 'contact' && <ContactSection />}

            <RetroFooter />
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
